import {
  DeepSignal,
  signalStoreFeature,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  StoreErrorState,
  StoreLoadingState,
  StoreSuccessState,
  useStoreCreationUtils,
} from './request-status.utils';

export type RequestStatusState<A extends string> = {
  errors: StoreErrorState<A>;
  loading: StoreLoadingState<A>;
  success: StoreSuccessState<A>;
};

export function withRequestStatus<A extends string>(actions: A[]) {
  const { state, handleActionPipe } = useStoreCreationUtils({
    actions,
  });

  return signalStoreFeature(
    withState<RequestStatusState<A>>(state),
    withMethods(() => ({
      handleActionPipe,
      isLoading: (action: A, id?: string): boolean => {
        if (id) {
          return state.loading[action]().includes(id);
        } else {
          return state.loading[action]().length > 0;
        }
      },
      hasError: (action: A, id?: string): boolean => {
        if (id) {
          return state.errors[action]().has(id);
        } else {
          return state.errors[action]().size > 0;
        }
      },
      getError: (action: A, id?: string) => {
        if (id) {
          return state.errors[action]().get(id);
        } else {
          return state.errors[action]().get(action);
        }
      },
      //Clear the error state for an action or a specific id
      clearError(action: A, id?: string) {
        if (id) {
          state.errors[action]().delete(id);
        } else {
          state.errors[action]().delete(action);
        }
      },
      /**
       * Returns true if the action was successful, false if it was not, and undefined if it has not been attempted.
       */
      getSuccess: (action: A, id?: string) => {
        if (id) {
          return state.success[action]().get(id);
        } else {
          return state.success[action]().get(action);
        }
      },
      // Clear the succes state for an action or a specific id
      clearSuccess(action: A, id?: string) {
        if (id) {
          state.success[action]().delete(id);
        } else {
          state.success[action]().delete(action);
        }
      },
    })),
  );
}

export type BaseStore<A extends string> = {
  errors: DeepSignal<StoreErrorState<A>>;
  loading: DeepSignal<StoreLoadingState<A>>;
  isLoading: (action: A, id?: string) => boolean;
  hasError: (action: A, id?: string) => boolean;
  getError: (action: A, id?: string) => unknown;
  getSuccess: (action: A, id?: string) => boolean | undefined;
};
